import { useEffect, useState } from "react";
import { format } from "date-fns";
import { FileText, Download } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { canAccessCentroDiurno } from "../../utils/centroDiurnoAccess";
import { getReportMensile } from "../../utils/centroDiurnoApi";
import PageHeader from "../../components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export default function CentroDiurnoReport() {
  const { token, user, activeStruttura } = useAuth();
  const [mese, setMese] = useState(format(new Date(), "yyyy-MM"));
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setReport(null);
  }, [activeStruttura?.id]);

  const loadReport = async () => {
    setLoading(true);
    try {
      const data = await getReportMensile(token, mese);
      setReport(data);
    } catch (e) {
      toast.error("Errore generazione report");
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = () => {
    if (!report?.righe?.length) return;
    const header = "Ospite,Presenze,Mezza giornata,Giornata intera,Extra giorni,Carnet scalati\n";
    const rows = report.righe
      .map(
        (r) =>
          `"${r.ospite_nome}",${r.presenze},${r.mezza_giornata},${r.giornata_intera},${r.extra_giorni},${r.carnet_scalati}`
      )
      .join("\n");
    const blob = new Blob([header + rows], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `report-centro-diurno-${mese}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!canAccessCentroDiurno(user)) {
    return <div className="p-8 text-center text-slate-600">Modulo non attivo</div>;
  }

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="p-4 md:p-6 lg:p-8 max-w-5xl mx-auto space-y-6">
        <PageHeader
          title="Report Centro Diurno"
          subtitle="Riepilogo mensile presenze e contabilità extra"
          icon={FileText}
          iconContainerClassName="bg-gradient-to-br from-emerald-500 to-emerald-700"
          iconClassName="text-white"
        />

        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="text-xs text-slate-500 block mb-1">Mese</label>
            <Input type="month" value={mese} onChange={(e) => setMese(e.target.value)} className="w-auto bg-white" />
          </div>
          <Button className="bg-[#4CAF50] hover:bg-[#43A047]" onClick={loadReport} disabled={loading}>
            Genera report
          </Button>
          {report && (
            <Button variant="outline" onClick={exportCsv}>
              <Download className="w-4 h-4 mr-1" /> Esporta CSV
            </Button>
          )}
        </div>

        {report && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <Card>
                <CardContent className="p-4">
                  <p className="text-xs text-slate-500">Totale presenze</p>
                  <p className="text-2xl font-bold">{report.totale_presenze}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <p className="text-xs text-slate-500">Giorni extra (fuori pacchetto)</p>
                  <p className="text-2xl font-bold text-amber-600">{report.totale_extra_giorni}</p>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Dettaglio per ospite — {mese}</CardTitle>
                <p className="text-xs text-slate-500">Include dati per fatturazione giorni aggiuntivi a fine mese</p>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Ospite</TableHead>
                      <TableHead className="text-right">Presenze</TableHead>
                      <TableHead className="text-right">Mezza g.</TableHead>
                      <TableHead className="text-right">Intera</TableHead>
                      <TableHead className="text-right">Extra</TableHead>
                      <TableHead className="text-right">Carnet</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(report.righe || []).map((r) => (
                      <TableRow key={r.fascicolo_id}>
                        <TableCell className="font-medium">{r.ospite_nome}</TableCell>
                        <TableCell className="text-right">{r.presenze}</TableCell>
                        <TableCell className="text-right">{r.mezza_giornata}</TableCell>
                        <TableCell className="text-right">{r.giornata_intera}</TableCell>
                        <TableCell className="text-right">{r.extra_giorni}</TableCell>
                        <TableCell className="text-right">{r.carnet_scalati}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
